import type { RouteObject } from 'react-router';
import { ApiaryLayout } from '../features/apiary/ApiaryLayout';
import { CatalogsPage } from '../features/apiary/pages/CatalogsPage';
import { ColoniesPage } from '../features/apiary/pages/ColoniesPage';
import { ColonyDetailPage } from '../features/apiary/pages/ColonyDetailPage';
import { FeedingsPage } from '../features/apiary/pages/FeedingsPage';
import { LocationDetailPage } from '../features/apiary/pages/LocationDetailPage';
import { PlantsPage } from '../features/forage/pages/PlantsPage';
import { LocationsPage } from '../features/apiary/pages/LocationsPage';
import { MiteCountsPage } from '../features/apiary/pages/MiteCountsPage';
import { TreatmentsPage } from '../features/apiary/pages/TreatmentsPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { BatchDetailPage } from '../features/harvest/pages/BatchDetailPage';
import { BatchesPage } from '../features/harvest/pages/BatchesPage';
import { StockPage } from '../features/harvest/pages/StockPage';
import { AuthorizePage } from '../features/identity/pages/AuthorizePage';
import { ForgotPasswordPage } from '../features/identity/pages/ForgotPasswordPage';
import { InvitationPage } from '../features/identity/pages/InvitationPage';
import { LoginPage } from '../features/identity/pages/LoginPage';
import { OnboardingPage } from '../features/identity/pages/OnboardingPage';
import { ResetPasswordPage } from '../features/identity/pages/ResetPasswordPage';
import { RequireSession } from '../features/identity/session';
import { BusinessSettingsPage } from '../features/identity/settings/BusinessSettingsPage';
import { ConnectionsPage } from '../features/identity/settings/ConnectionsPage';
import { MemberDetailPage } from '../features/identity/settings/MemberDetailPage';
import { MembersPage } from '../features/identity/settings/MembersPage';
import { ProfilePage } from '../features/identity/settings/ProfilePage';
import { RoleEditorPage } from '../features/identity/settings/RoleEditorPage';
import { RolesPage } from '../features/identity/settings/RolesPage';
import { SessionsPage } from '../features/identity/settings/SessionsPage';
import { SettingsIndex, SettingsLayout } from '../features/identity/settings/SettingsLayout';
import { CustomerDetailPage } from '../features/crm/pages/CustomerDetailPage';
import { CustomersPage } from '../features/crm/pages/CustomersPage';
import { InvoiceDetailPage } from '../features/sales/pages/InvoiceDetailPage';
import { InvoicesPage } from '../features/sales/pages/InvoicesPage';
import { MarketDayPage } from '../features/sales/pages/MarketDayPage';
import { MarketDaysPage } from '../features/sales/pages/MarketDaysPage';
import { MarketPage } from '../features/sales/pages/MarketPage';
import { MarketsPage } from '../features/sales/pages/MarketsPage';
import { MarketModePage } from '../features/sales/pages/MarketModePage';
import { PricesPage } from '../features/sales/pages/PricesPage';
import { SaleDetailPage } from '../features/sales/pages/SaleDetailPage';
import { SalesPage } from '../features/sales/pages/SalesPage';
import { SalesSettingsPage } from '../features/sales/pages/SalesSettingsPage';
import { SalesLayout } from '../features/sales/SalesLayout';
import { FinanceLayout } from '../features/finance/FinanceLayout';
import { EntriesPage } from '../features/finance/pages/EntriesPage';
import { EntryDetailPage } from '../features/finance/pages/EntryDetailPage';
import { FinanceCategoriesPage } from '../features/finance/pages/FinanceCategoriesPage';
import { FinanceOverviewPage } from '../features/finance/pages/FinanceOverviewPage';
import { NotificationsPage } from '../features/tasks/pages/NotificationsPage';
import { TaskDetailPage } from '../features/tasks/pages/TaskDetailPage';
import { TasksPage } from '../features/tasks/pages/TasksPage';
import { SharePage } from '../features/pwa/SharePage';
import { NotFoundPage } from '../features/system/NotFoundPage';
import { AppLayout } from './AppLayout';
import { MorePage } from './MorePage';
import { ReportsLayout } from '../features/reports/ReportsLayout';
import { DataExportPage } from '../features/reports/pages/DataExportPage';
import { AboutPage } from '../features/system/AboutPage';
import { ReportsExportPage } from '../features/reports/pages/ReportsExportPage';
import { SalesStatsPage } from '../features/reports/pages/SalesStatsPage';
import { YieldPage } from '../features/reports/pages/YieldPage';

/**
 * Route table. Each feature module (= bounded context, WEB-019) contributes
 * its pages here. Paths are German, matching the links in e-mails.
 */
export const routes: RouteObject[] = [
  // Without session
  { path: '/anmelden', element: <LoginPage /> },
  { path: '/passwort-vergessen', element: <ForgotPasswordPage /> },
  { path: '/passwort-zuruecksetzen', element: <ResetPasswordPage /> },
  { path: '/einladung', element: <InvitationPage /> },

  // OAuth authorization endpoint for MCP clients (MCP-003)
  {
    path: '/oauth/authorize',
    element: (
      <RequireSession>
        <AuthorizePage />
      </RequireSession>
    ),
  },

  // With session, full screen (market mode on a tablet, MKT-001)
  {
    path: '/markt/:marketDayId',
    element: (
      <RequireSession>
        <MarketModePage />
      </RequireSession>
    ),
  },

  // With session
  {
    element: (
      <RequireSession>
        <AppLayout />
      </RequireSession>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'willkommen', element: <OnboardingPage /> },
      { path: 'teilen', element: <SharePage /> },
      {
        path: 'voelker',
        element: <ApiaryLayout />,
        children: [
          { index: true, element: <ColoniesPage /> },
          { path: 'behandlungen', element: <TreatmentsPage /> },
          { path: 'milbenkontrollen', element: <MiteCountsPage /> },
          { path: 'fuetterungen', element: <FeedingsPage /> },
        ],
      },
      { path: 'voelker/:colonyId', element: <ColonyDetailPage /> },
      { path: 'standorte', element: <LocationsPage /> },
      { path: 'standorte/trachtpflanzen', element: <PlantsPage /> },
      { path: 'standorte/:locationId', element: <LocationDetailPage /> },
      { path: 'ernte', element: <BatchesPage /> },
      { path: 'ernte/:batchId', element: <BatchDetailPage /> },
      { path: 'bestand', element: <StockPage /> },
      { path: 'kunden', element: <CustomersPage /> },
      { path: 'kunden/:customerId', element: <CustomerDetailPage /> },
      {
        path: 'verkauf',
        element: <SalesLayout />,
        children: [
          { index: true, element: <SalesPage /> },
          { path: 'rechnungen', element: <InvoicesPage /> },
          { path: 'maerkte', element: <MarketsPage /> },
          { path: 'markttage', element: <MarketDaysPage /> },
          { path: 'preise', element: <PricesPage /> },
        ],
      },
      { path: 'verkauf/:saleId', element: <SaleDetailPage /> },
      { path: 'verkauf/rechnungen/:invoiceId', element: <InvoiceDetailPage /> },
      { path: 'verkauf/maerkte/:marketId', element: <MarketPage /> },
      { path: 'verkauf/markttage/:marketDayId', element: <MarketDayPage /> },
      { path: 'aufgaben', element: <TasksPage /> },
      { path: 'aufgaben/:taskId', element: <TaskDetailPage /> },
      {
        path: 'finanzen',
        element: <FinanceLayout />,
        children: [
          { index: true, element: <FinanceOverviewPage /> },
          { path: 'buchungen', element: <EntriesPage /> },
          { path: 'kategorien', element: <FinanceCategoriesPage /> },
        ],
      },
      { path: 'finanzen/buchungen/:entryId', element: <EntryDetailPage /> },
      {
        path: 'auswertungen',
        element: <ReportsLayout />,
        children: [
          { index: true, element: <YieldPage /> },
          { path: 'verkauf', element: <SalesStatsPage /> },
          { path: 'berichte', element: <ReportsExportPage /> },
        ],
      },
      {
        path: 'mehr',
        element: <MorePage />,
      },
      {
        path: 'einstellungen',
        element: <SettingsLayout />,
        children: [
          { index: true, element: <SettingsIndex /> },
          { path: 'imkerei', element: <BusinessSettingsPage /> },
          { path: 'mitglieder', element: <MembersPage /> },
          { path: 'mitglieder/:userId', element: <MemberDetailPage /> },
          { path: 'rollen', element: <RolesPage /> },
          { path: 'kataloge', element: <CatalogsPage /> },
          { path: 'verkauf', element: <SalesSettingsPage /> },
          { path: 'rollen/neu', element: <RoleEditorPage /> },
          { path: 'rollen/:roleId', element: <RoleEditorPage /> },
          { path: 'profil', element: <ProfilePage /> },
          { path: 'benachrichtigungen', element: <NotificationsPage /> },
          { path: 'sitzungen', element: <SessionsPage /> },
          { path: 'verbundene-apps', element: <ConnectionsPage /> },
          { path: 'datenexport', element: <DataExportPage /> },
          { path: 'ueber', element: <AboutPage /> },
        ],
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
];
